<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { downloadUserAuditCsv } from '../../utils/auditExport';

const { t } = useI18n();

// Hidden, unlinked page (route /audit/export). Reachable only by typing
// the URL; it's auth-gated by the router's authGuard like every other
// route, so an unauthenticated visitor is bounced to the /login splash
// before this ever renders. The button fetches GET /api/audit/export
// with the Bearer token attached (via the /api/* interceptor) and saves
// the CSV — see utils/auditExport.ts for why a plain navigation can't.

const exporting = ref(false);
const error = ref<string | null>(null);

const onExport = async () => {
    if (exporting.value) return;
    exporting.value = true;
    error.value = null;
    try {
        await downloadUserAuditCsv();
    } catch (err) {
        console.error('Failed to export user audit log', err);
        error.value = t('misc.auditExportError');
    } finally {
        exporting.value = false;
    }
};
</script>

<template>
    <main class="audit-export-page">
        <div class="audit-export-card">
            <h1 class="audit-export-title">{{ t('misc.auditExportTitle') }}</h1>
            <button
                type="button"
                class="audit-export-button"
                data-testid="export-audit"
                :disabled="exporting"
                @click="onExport"
            >
                <span class="material-icons">download</span>
                <span>{{ exporting ? t('misc.auditExporting') : t('misc.auditExportButton') }}</span>
            </button>
            <p v-if="error" class="audit-export-error" role="alert">
                {{ error }}
            </p>
        </div>
    </main>
</template>

<style scoped>
.audit-export-page {
    display: flex;
    justify-content: center;
    padding: 48px 16px;
}

.audit-export-card {
    width: 100%;
    max-width: 480px;
    background: var(--color-bg-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    padding: 28px;
    display: flex;
    flex-direction: column;
    align-items:center;
    gap: 14px;
}

.audit-export-title {
    margin: 0;
    font-size: var(--text-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
}

.audit-export-blurb {
    margin: 0;
    color: var(--color-text-secondary);
    font-size: var(--text-base);
}

.audit-export-button {
    align-self: center;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: var(--color-secondary);
    color: var(--color-bg-white);
    border: none;
    border-radius: var(--radius-md);
    cursor: pointer;
    font-size: var(--text-base);
    font-weight: var(--font-weight-medium);
}

.audit-export-button:hover:not(:disabled),
.audit-export-button:focus-visible:not(:disabled) {
    background: var(--color-secondary-hover);
    outline: none;
}

.audit-export-button:disabled {
    cursor: default;
    opacity: 0.6;
}

.audit-export-button .material-icons {
    font-size: 18px;
}

.audit-export-error {
    margin: 0;
    color: var(--color-danger, #c0392b);
    font-size: var(--text-sm);
}
</style>
